import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useArticleDateUtilities } from "scmp-app/components/article/article-date/hooks";
import { PlusWidgetTheme } from "scmp-app/components/plus/plus-widget";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { plusWidgetDailyPulseQuery$key } from "scmp-app/queries/__generated__/plusWidgetDailyPulseQuery.graphql";

import {
  Container,
  HeadlineLink,
  LabelContainer,
  LastUpdatedDate,
  ReadFullDailyPulseLink,
  StyledContentHeadline,
} from "./styles";

type Props = {
  className?: string;
  pageType: "homepage" | "section" | "topic";
  plusWidgetTheme: PlusWidgetTheme;
  reference: plusWidgetDailyPulseQuery$key;
};
export const PlusWidgetDailyPulse: FunctionComponent<Props> = ({
  className,
  pageType,
  plusWidgetTheme = PlusWidgetTheme.DARK,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment plusWidgetDailyPulseQuery on Query
      @argumentDefinitions(dailyPulseTypeUuId: { type: "String!" }) {
        dailyPulseQuery: articles(
          filter: { typeIds: [$dailyPulseTypeUuId] }
          first: 1
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              entityId
              urlAlias
              ...entityLink
              ...contentHeadlineContent
              ...hooksArticleDateUtilsContent
              ...hooksPlusArticleLinkHandlerArticle
            }
          }
        }
      }
    `,
    reference,
  );

  const topArticle = first(content.dailyPulseQuery.edges)?.node;
  const { lastUpdatedDate } = useArticleDateUtilities(topArticle, "h:mma, d MMM yyyy");

  const handleButtonClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: topArticle?.entityId ?? "",
        block_section: "daily_pulse",
        destination_url: topArticle?.urlAlias ?? "",
        page_type: pageType,
        widget_name: "content_discovery",
        widget_version: "subscriber",
      },
      subcategory: "widget",
    });
  };

  if (!topArticle) return null;

  const renderHeadline = () => {
    if (plusWidgetTheme === PlusWidgetTheme.LIGHT) {
      return (
        <HeadlineLink onClick={handleButtonClickEvent} reference={topArticle}>
          <StyledContentHeadline preferSocialHeadline reference={topArticle} />
        </HeadlineLink>
      );
    }
    return <StyledContentHeadline preferSocialHeadline reference={topArticle} />;
  };

  return (
    <Container $plusWidgetTheme={plusWidgetTheme} className={className}>
      <LabelContainer
        pathname={`/plus?plus-detail=daily-pulse&module=content_discovery&pgtype=${pageType}`}
      >
        DAILY PULSE
      </LabelContainer>
      {renderHeadline()}
      <LastUpdatedDate>
        Last updated: <span>{lastUpdatedDate}</span>
      </LastUpdatedDate>
      <ReadFullDailyPulseLink onClick={handleButtonClickEvent} reference={topArticle}>
        READ THE DAILY PULSE
      </ReadFullDailyPulseLink>
    </Container>
  );
};

PlusWidgetDailyPulse.displayName = "PlusWidgetDailyPulse";
